/* ============================================================
   La Nuit — header.js
   Site header interactions: sticky on scroll, mobile menu drawer,
   search overlay, cart count live-update.
   Relies on window.LaNuit set by global.js (which loads first).
   ============================================================ */

(function () {
  const header = document.querySelector('[data-section-id][data-sticky]');

  /* ---------- Sticky on scroll ---------- */

  if (header && header.dataset.sticky === 'true') {
    let ticking = false;
    const onScroll = () => {
      header.classList.toggle('is-stuck', window.scrollY > 80);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ---------- Mobile menu drawer ---------- */

  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const mobileToggle = document.querySelector('[data-mobile-menu-toggle]');
  const mobileClose = document.querySelector('[data-mobile-menu-close]');
  let releaseMenuFocus = null;

  function openMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.removeAttribute('hidden');
    requestAnimationFrame(() => mobileMenu.classList.add('is-open'));
    mobileToggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    releaseMenuFocus = window.LaNuit?.trapFocus(mobileMenu, closeMobileMenu);
  }

  function closeMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('is-open');
    mobileToggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    releaseMenuFocus?.();
    releaseMenuFocus = null;
    setTimeout(() => mobileMenu.setAttribute('hidden', ''), 320);
    mobileToggle?.focus();
  }

  mobileToggle?.addEventListener('click', openMobileMenu);
  mobileClose?.addEventListener('click', closeMobileMenu);

  /* ---------- Logged-in account menu (dropdown on desktop, bottom sheet on mobile) ---------- */

  const accountMenu = document.querySelector('[data-account-menu]');
  const accountTrigger = accountMenu?.querySelector('[data-account-menu-trigger]');
  const accountPanel = accountMenu?.querySelector('[data-account-menu-panel]');
  const accountBackdrop = accountMenu?.querySelector('[data-account-menu-backdrop]');
  const accountCloseBtn = accountMenu?.querySelector('[data-account-menu-close]');

  // 740px matches the CSS breakpoint that switches between dropdown and bottom sheet.
  const isMobileSheet = () => window.matchMedia('(max-width: 740px)').matches;

  function openAccountMenu() {
    if (!accountPanel || !accountTrigger) return;
    accountPanel.classList.remove('is-closing');
    accountBackdrop?.classList.remove('is-closing');
    accountPanel.removeAttribute('hidden');
    accountBackdrop?.removeAttribute('hidden');
    accountTrigger.setAttribute('aria-expanded', 'true');
    if (isMobileSheet()) {
      // Lock body scroll while the bottom sheet is open.
      document.documentElement.setAttribute('data-account-open', '');
    }
    // First focusable item inside the panel gets focus.
    const first = accountPanel.querySelector('a, button');
    first?.focus({ preventScroll: true });
    document.addEventListener('keydown', onAccountKey);
    // Desktop dropdown uses outside-click; the mobile sheet uses its backdrop click instead.
    if (!isMobileSheet()) {
      document.addEventListener('click', onAccountOutsideClick, true);
    }
  }

  function closeAccountMenu(restoreFocus) {
    if (!accountPanel || !accountTrigger) return;
    accountTrigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onAccountKey);
    document.removeEventListener('click', onAccountOutsideClick, true);
    document.documentElement.removeAttribute('data-account-open');

    if (isMobileSheet()) {
      // Animate the sheet + backdrop out, then hide them after the transition.
      accountPanel.classList.add('is-closing');
      accountBackdrop?.classList.add('is-closing');
      const finish = () => {
        accountPanel.classList.remove('is-closing');
        accountBackdrop?.classList.remove('is-closing');
        accountPanel.setAttribute('hidden', '');
        accountBackdrop?.setAttribute('hidden', '');
        accountPanel.removeEventListener('animationend', finish);
      };
      accountPanel.addEventListener('animationend', finish);
      // Safety net in case animationend doesn't fire (e.g. prefers-reduced-motion).
      setTimeout(finish, 280);
    } else {
      accountPanel.setAttribute('hidden', '');
      accountBackdrop?.setAttribute('hidden', '');
    }
    if (restoreFocus) accountTrigger.focus({ preventScroll: true });
  }

  function onAccountKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeAccountMenu(true); }
  }
  function onAccountOutsideClick(e) {
    if (!accountMenu.contains(e.target)) closeAccountMenu(false);
  }

  accountTrigger?.addEventListener('click', (e) => {
    e.preventDefault();
    const open = accountTrigger.getAttribute('aria-expanded') === 'true';
    if (open) closeAccountMenu(true); else openAccountMenu();
  });

  // Mobile-only: backdrop tap + close-button click + viewport resize re-check.
  accountBackdrop?.addEventListener('click', () => closeAccountMenu(true));
  accountCloseBtn?.addEventListener('click', () => closeAccountMenu(true));

  // If the user resizes across the breakpoint while the menu is open, close it
  // so we don't get a half-open sheet stranded on the desktop layout.
  window.addEventListener('resize', () => {
    if (accountTrigger?.getAttribute('aria-expanded') === 'true') closeAccountMenu(false);
  });

  /* ---------- Search overlay ---------- */

  const searchOverlay = document.querySelector('[data-search-overlay]');
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchCloseBtn = document.querySelector('[data-search-close]');
  const searchInput = searchOverlay?.querySelector('[data-search-input]');
  const searchClearBtn = searchOverlay?.querySelector('[data-search-clear]');
  const searchTrending = searchOverlay?.querySelector('[data-search-trending]');
  const searchBackdrop = searchOverlay?.querySelector('[data-search-backdrop]');
  let releaseSearchFocus = null;

  const isMobileSearch = () => window.matchMedia('(max-width: 740px)').matches;

  function syncSearchUI() {
    if (!searchInput) return;
    const hasText = searchInput.value.trim().length > 0;
    // Clear button only when there's text.
    if (searchClearBtn) {
      if (hasText) searchClearBtn.removeAttribute('hidden');
      else searchClearBtn.setAttribute('hidden', '');
    }
    // Trending chips hide once the user types (predictive results take over).
    if (searchTrending) {
      if (hasText) searchTrending.setAttribute('hidden', '');
      else searchTrending.removeAttribute('hidden');
    }
  }

  function openSearch() {
    if (!searchOverlay) return;
    searchOverlay.classList.remove('is-closing');
    searchOverlay.removeAttribute('hidden');
    if (isMobileSearch()) {
      document.documentElement.setAttribute('data-search-open', '');
    }
    syncSearchUI();
    // Focus the input after the open animation kicks off.
    setTimeout(() => searchInput?.focus({ preventScroll: true }), 60);
    releaseSearchFocus = window.LaNuit?.trapFocus(searchOverlay, () => closeSearch(true));
    document.addEventListener('keydown', onSearchKey);
  }

  function closeSearch(restoreFocus) {
    if (!searchOverlay) return;
    document.removeEventListener('keydown', onSearchKey);
    releaseSearchFocus?.();
    releaseSearchFocus = null;
    document.documentElement.removeAttribute('data-search-open');

    // Animate out, then hide. Safety timeout in case animationend doesn't fire.
    searchOverlay.classList.add('is-closing');
    const finish = () => {
      searchOverlay.classList.remove('is-closing');
      searchOverlay.setAttribute('hidden', '');
      searchOverlay.removeEventListener('animationend', finish);
    };
    searchOverlay.addEventListener('animationend', finish);
    setTimeout(finish, 280);

    if (restoreFocus) searchToggle?.focus({ preventScroll: true });
  }

  function onSearchKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeSearch(true); }
  }

  searchToggle?.addEventListener('click', openSearch);
  searchCloseBtn?.addEventListener('click', () => closeSearch(true));
  searchBackdrop?.addEventListener('click', () => closeSearch(true));
  searchClearBtn?.addEventListener('click', () => {
    if (!searchInput) return;
    searchInput.value = '';
    syncSearchUI();
    searchInput.focus({ preventScroll: true });
    // Notify any predictive-search element to clear its results.
    searchInput.dispatchEvent(new Event('input', { bubbles: true }));
  });
  searchInput?.addEventListener('input', syncSearchUI);

  // Trending chips fill the box and let predictive search take over.
  searchOverlay?.querySelectorAll('[data-search-term]').forEach((chip) => {
    chip.addEventListener('click', () => {
      if (!searchInput) return;
      searchInput.value = chip.getAttribute('data-search-term') || '';
      syncSearchUI();
      searchInput.focus({ preventScroll: true });
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });

  // ----- Scope chips -----
  // The overlay's suggestions filter on the chip's product type in
  // predictive-search.js; the full search page receives it as Shopify's
  // `product_type:"X"` field operator (the only syntax /search honours;
  // `type:"X"` was read as plain words and scoped nothing).
  const searchScopeChips = searchOverlay?.querySelectorAll('[data-search-scope]');
  let activeScope = '';
  let activeScopeLabel = '';

  searchScopeChips?.forEach((chip) => {
    if (chip.classList.contains('is-active')) {
      activeScope = chip.getAttribute('data-search-scope') || '';
      activeScopeLabel = activeScope ? chip.textContent.trim() : '';
    }
    chip.addEventListener('click', () => {
      searchScopeChips.forEach((c) => {
        c.classList.remove('is-active');
        c.setAttribute('aria-pressed', 'false');
      });
      chip.classList.add('is-active');
      chip.setAttribute('aria-pressed', 'true');
      activeScope = chip.getAttribute('data-search-scope') || '';
      activeScopeLabel = activeScope ? chip.textContent.trim() : '';
      // Re-fire input event so predictive-search refreshes with the new scope.
      searchInput?.focus({ preventScroll: true });
      searchInput?.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });

  // One builder for every route into /search: the scope as a field
  // operator plus `-tag:internal-only`, which hides raw-material products
  // server-side (main-search.liquid strips both back out for display).
  const searchForm = searchOverlay?.querySelector('[data-search-form]');
  function searchPageUrl(term, scope) {
    const url = new URL(searchForm?.action || '/search', window.location.origin);
    let q = (term || '').trim();
    if (scope) q += ' product_type:"' + scope + '"';
    q += ' -tag:internal-only';
    url.searchParams.set('q', q);
    url.searchParams.set('type', 'product');
    return url.toString();
  }

  searchForm?.addEventListener('submit', (e) => {
    if (!searchInput) return;
    const q = (searchInput.value || '').trim();
    if (!q) return;
    e.preventDefault();
    window.location.href = searchPageUrl(q, activeScope);
  });

  // Expose the active scope to predictive-search so suggestions honour it.
  window.LaNuit = window.LaNuit || {};
  Object.defineProperty(window.LaNuit, 'searchScope', { get: () => activeScope, configurable: true });
  Object.defineProperty(window.LaNuit, 'searchScopeLabel', { get: () => activeScopeLabel, configurable: true });
  window.LaNuit.searchPageUrl = searchPageUrl;

  /* ---------- Chip click feedback (trending + recent) ----------
     Plain <a> chips navigate to /search?q=… but on slow networks the
     page transition can take 500–1500ms with no visual response, so
     people tap the chip multiple times. We immediately mark the chip
     as loading, add a progress bar to the overlay, and disable further
     clicks while the browser fetches the next page. */

  /* Prefetch the chip's destination on hover / touchstart so the actual
     click navigation is near-instant. Each URL is prefetched at most once
     per session. Falls back gracefully if <link rel=prefetch> is unsupported. */
  const prefetched = new Set();
  function prefetchUrl(url) {
    if (!url || prefetched.has(url)) return;
    prefetched.add(url);
    try {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    } catch (_) { /* noop */ }
  }
  function maybePrefetchChip(e) {
    const chip = e.target.closest && e.target.closest('.site-header__search-chip');
    if (chip?.href) prefetchUrl(chip.href);
  }
  searchOverlay?.addEventListener('mouseover', maybePrefetchChip, { passive: true });
  searchOverlay?.addEventListener('touchstart', maybePrefetchChip, { passive: true });
  searchOverlay?.addEventListener('focusin', maybePrefetchChip);

  function handleChipClick(e) {
    const chip = e.target.closest('.site-header__search-chip');
    // Trending and recent chips are buttons that fill the box; only a
    // chip that navigates (a real link) gets the loading lock, otherwise
    // the first tap locked the overlay and every later chip tap was
    // swallowed as a "duplicate".
    if (!chip || !chip.href || !searchOverlay) return;
    // Cmd/Ctrl-click or middle-click → let the browser open in a new tab as usual.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    if (searchOverlay.classList.contains('is-loading')) {
      // Already navigating from a prior tap — block duplicates.
      e.preventDefault();
      return;
    }
    searchOverlay.classList.add('is-loading');
    chip.classList.add('is-loading');
    // Reflect the chosen term in the input so the modal doesn't look empty
    // during the transition.
    const label = chip.querySelector('span:not([aria-hidden])')?.textContent?.trim();
    if (searchInput && label) searchInput.value = label;
  }
  searchOverlay?.addEventListener('click', handleChipClick);

  /* ---------- Cart count live update ---------- */

  document.addEventListener('cart:updated', (e) => {
    const count = e.detail?.item_count ?? 0;
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
  });
})();
